import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StationPanel from "@/components/StationPanel";
import DirectionsButton from "@/components/DirectionsButton";
import { connectorLabels } from "@/lib/connectors";
import { ConfidenceBadge, SourceChip, StatusPill } from "@/components/badges";
import CoverageMap from "@/components/CoverageMap";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { ArrowLeftIcon, ClockIcon, ShieldCheckIcon, GitBranchIcon, TrendUpIcon, PencilSimpleIcon, ClipboardTextIcon, RoadHorizonIcon } from "@phosphor-icons/react";

function CompatChip({ label, ok }) {
  const cls = ok
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : ok === false
    ? "bg-slate-50 text-slate-400 border-slate-200 line-through"
    : "bg-slate-50 text-slate-400 border-slate-200";
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls}`}>{label}</span>;
}

function Bar({ label, value }) {
  return (
    <div>
      <div className="flex justify-between text-xs text-slate-500"><span>{label}</span><span className="font-mono">{value}</span></div>
      <div className="mt-1 h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-emerald-500" style={{ width: `${value}%` }} /></div>
    </div>
  );
}

function Field({ label, value, prov }) {
  return (
    <div className="border-b border-slate-100 py-2.5">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-0.5 flex flex-wrap items-center gap-2">
        <span className="text-sm text-slate-800 font-mono">{value ?? "—"}</span>
        {prov && <SourceChip name={prov.source_name} type={prov.source_type} />}
      </div>
    </div>
  );
}

export default function SiteDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "analyst";
  const [data, setData] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const loadDetail = () => api.get(`/sites/${id}`).then((r) => setData(r.data));
  useEffect(() => { loadDetail(); }, [id]);
  if (!data) return <div className="p-8 text-slate-400 font-mono text-sm">Loading…</div>;

  const s = data.site;
  const fs = s.field_sources || {};
  const c = s.confidence || {};
  const trend = [...(data.confidence_history || [])]
    .sort((a, b) => new Date(a.computed_at) - new Date(b.computed_at))
    .map((h) => ({ t: new Date(h.computed_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }), score: h.overall_score }));

  return (
    <div className="p-5 lg:p-8 space-y-5">
      <Link to="/sites" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-600"><ArrowLeftIcon size={16} /> Back to sites</Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-semibold text-slate-900">{s.name}</h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-slate-500">
            <StatusPill status={s.status} /> · {s.address}
            {s.is_fictional && <span className="rounded bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">FICTIONAL SEED</span>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <ConfidenceBadge label={c.label} score={c.overall_score} className="text-sm px-3 py-1" />
          <div className="flex flex-wrap justify-end gap-2">
            <DirectionsButton lat={s.latitude} lng={s.longitude} />
            <button data-testid="audit-this-btn" onClick={() => nav(`/audit?site=${id}`)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
              <ClipboardTextIcon size={16} /> Audit station
            </button>
            {canEdit && (
              <button data-testid="edit-add-photos-btn" onClick={() => setPanelOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors">
                <PencilSimpleIcon size={16} /> Edit &amp; add photos
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="font-display font-medium text-slate-900 mb-2">Canonical Fields & Provenance</h2>
            <div className="grid sm:grid-cols-2 gap-x-6">
              <Field label="Operator" value={s.operator} prov={fs.operator} />
              <Field label="Power (kW)" value={s.power_kw} prov={fs.power_kw} />
              <Field label="Tariff ₹/kWh" value={s.tariff_per_kwh} prov={fs.tariff_per_kwh} />
              <div className="border-b border-slate-100 py-2.5">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Connectors</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2">
                  <span className="text-sm text-slate-800 font-mono">{connectorLabels(s.connector_types) || "—"}</span>
                  {s.connector_needs_review && <span className="rounded bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">NEEDS REVIEW</span>}
                </div>
              </div>
              <Field label="Access type" value={s.access_type || "Public"} />
              <Field label="Address" value={s.address} prov={fs.address} />
            </div>
          </div>

          {s.enrichment && (
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="highway-enrichment-card">
              <h2 className="font-display font-medium text-slate-900 mb-2 flex items-center gap-2"><RoadHorizonIcon size={18} /> Highway &amp; Compatibility</h2>
              <div className="grid sm:grid-cols-2 gap-x-6">
                <Field label="Highway proximity" value={s.enrichment.highway_proximity_status} />
                <Field label="Highway reference" value={s.enrichment.highway_reference} />
                <Field label="Charger rating (kW)" value={s.enrichment.charger_rating_kw} />
                <Field label="No. of connectors" value={s.enrichment.no_of_connectors} />
                <Field label="Ownership" value={s.enrichment.govt_or_private} />
                <Field label="Validation confidence" value={s.enrichment.validation_confidence} />
                <div className="border-b border-slate-100 py-2.5 sm:col-span-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Vehicle compatibility</div>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    <CompatChip label="2W" ok={s.enrichment.vehicle_compat?.two_w} />
                    <CompatChip label="3W" ok={s.enrichment.vehicle_compat?.three_w} />
                    <CompatChip label="4W" ok={s.enrichment.vehicle_compat?.four_w} />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="font-display font-medium text-slate-900 mb-2 flex items-center gap-2"><TrendUpIcon size={18} /> Confidence Trend</h2>
            {trend.length < 2 ? (
              <div className="text-sm text-slate-400">Not enough history yet — the trend builds as new evidence arrives.</div>
            ) : (
              <div style={{ height: 200 }} data-testid="confidence-trend">
                <ResponsiveContainer>
                  <AreaChart data={trend} margin={{ top: 5, right: 8, bottom: 0, left: -12 }}>
                    <defs>
                      <linearGradient id="confGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#059669" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#059669" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="t" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} width={34} />
                    <Tooltip />
                    <Area type="monotone" dataKey="score" stroke="#059669" strokeWidth={2} fill="url(#confGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100"><h2 className="font-display font-medium text-slate-900">Location</h2></div>
            <CoverageMap sites={[s]} center={{ lat: s.latitude, lng: s.longitude }} height={260} />
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="font-display font-medium text-slate-900 mb-3 flex items-center gap-2"><ClockIcon size={18} /> Status Event Ledger</h2>
            <div className="space-y-2">
              {data.status_events.map((e) => (
                <div key={e.id} className="flex items-center justify-between text-sm border-b border-slate-50 pb-2">
                  <StatusPill status={e.status} />
                  <span className="font-mono text-xs text-slate-400">{new Date(e.at).toLocaleString()}</span>
                </div>
              ))}
              {data.status_events.length === 0 && <div className="text-sm text-slate-400">No events.</div>}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="font-display font-medium text-slate-900 mb-3 flex items-center gap-2"><ShieldCheckIcon size={18} /> Confidence Breakdown</h2>
            <div className="space-y-3">
              <Bar label="Location" value={c.location_confidence || 0} />
              <Bar label="Data" value={c.data_confidence || 0} />
              <Bar label="Operational" value={c.operational_confidence || 0} />
            </div>
            <ul className="mt-4 space-y-1.5 text-xs text-slate-500">
              {(c.reasons || []).map((r, i) => <li key={i} className="flex gap-1.5"><span className="text-emerald-500">•</span>{r}</li>)}
            </ul>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="font-display font-medium text-slate-900 mb-2">Contributing Sources</h2>
            <div className="flex flex-wrap gap-1.5">
              {data.sources.map((src) => <SourceChip key={src.id} name={src.name} type={src.type} />)}
            </div>
          </div>

          {data.discrepancies.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
              <h2 className="font-display font-medium text-amber-800 mb-2 flex items-center gap-2"><GitBranchIcon size={18} /> Open Discrepancies</h2>
              {data.discrepancies.filter((d) => d.status === "open").map((d) => (
                <div key={d.id} className="text-xs text-amber-800 border-b border-amber-100 py-1.5">
                  <span className="font-semibold">{d.field}</span>: {String(d.existing_value)} → {String(d.incoming_value)}
                </div>
              ))}
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="font-display font-medium text-slate-900 mb-2">Field Audits</h2>
            {data.audits.length === 0 ? <div className="text-sm text-slate-400">No audits yet.</div> :
              data.audits.map((a) => (
                <div key={a.id} className="text-xs text-slate-600 border-b border-slate-100 py-2">
                  <div className="font-medium text-slate-800">{a.auditor_name}</div>
                  <div className="text-slate-400 font-mono">{new Date(a.submitted_at).toLocaleString()}</div>
                  <div className="mt-1">Operational: {a.chargers_operational ? "✓" : "✗"} · {a.working_connectors}/{a.total_connectors} connectors</div>
                  {a.notes && <div className="mt-1 italic text-slate-500">"{a.notes}"</div>}
                </div>
              ))}
          </div>
        </div>
      </div>

      <StationPanel open={panelOpen} mode="view" siteId={id} startEditing onClose={() => setPanelOpen(false)} onSaved={loadDetail} />
    </div>
  );
}
